import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, AtSign, Shield, Copy, Check, Share2, Edit3, Lock, Loader2, Save, X, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import CreatorsSection from '../components/CreatorsSection';
import ProfilePictureCard from '../components/ProfilePictureCard';
import { api } from '../lib/api';
import { toast } from '../hooks/use-toast';

export default function Profile() {
  const navigate = useNavigate();
  const { user, isAuthed, isAdmin, openAuth, refresh } = useAuth();
  const [copied, setCopied] = useState(false);

  const profileUrl = user?.username && typeof window !== 'undefined'
    ? `${window.location.origin}/u/${user.username}`
    : null;

  const copyProfileLink = async () => {
    if (!profileUrl) return;
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      toast({ title: 'Profile link copied!', description: 'Paste it in Discord, Messenger, or a TikTok caption — friends can jump straight to your trade page.' });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast({ title: 'Copy failed', description: 'Long-press and copy manually.' });
    }
  };

  const shareProfile = async () => {
    if (!profileUrl) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${user?.name || user?.username}'s Sprite Vault`,
          text: `Trade with me on Sprite Vault — @${user.username}`,
          url: profileUrl,
        });
      } else {
        await copyProfileLink();
      }
    } catch { /* user cancelled */ }
  };

  if (!isAuthed) {
    return (
      <div
        className="min-h-screen"
        style={{
          background:
            'radial-gradient(circle at 10% 0%, #ff2bd6 0%, transparent 40%),' +
            'radial-gradient(circle at 100% 20%, #00b8ff 0%, transparent 40%),' +
            'radial-gradient(circle at 50% 100%, #ffd900 0%, transparent 45%),' +
            'linear-gradient(180deg, #ffe9fb 0%, #fff7d6 60%, #e9f7ff 100%)',
        }}
      >
        <div className="max-w-3xl mx-auto px-3 sm:px-6 py-16 text-center">
          <div className="rounded-2xl bg-white border-[3px] border-[#3a1064] shadow-[0_8px_0_#3a1064] p-8">
            <User className="h-12 w-12 mx-auto text-[#ff2bd6]" />
            <div className="mt-3 font-black text-2xl text-[#3a1064]">Sign in to view your profile</div>
            <p className="mt-2 font-bold text-[#3a1064]/80 text-sm">
              Your profile shows your creator bonus entries, submitted posts, and account details.
            </p>
            <button
              data-testid="profile-signin-btn"
              onClick={() => openAuth('login')}
              className="mt-4 h-11 px-5 rounded-xl bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black border-[3px] border-[#3a1064] shadow-[0_4px_0_#3a1064]"
            >
              Sign in
            </button>
            <div>
              <button
                onClick={() => navigate('/')}
                className="mt-4 text-xs font-black text-[#3a1064]/70 hover:text-[#3a1064] underline"
              >
                ← Back to tracker
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const initial = (user?.name || user?.email || '?').charAt(0).toUpperCase();

  return (
    <div
      className="min-h-screen"
      style={{
        background:
          'radial-gradient(circle at 10% 0%, #ff2bd6 0%, transparent 40%),' +
          'radial-gradient(circle at 100% 20%, #00b8ff 0%, transparent 40%),' +
          'radial-gradient(circle at 50% 100%, #ffd900 0%, transparent 45%),' +
          'linear-gradient(180deg, #ffe9fb 0%, #fff7d6 60%, #e9f7ff 100%)',
      }}
    >
      <div className="max-w-3xl mx-auto px-3 sm:px-6 py-6 sm:py-10">
        <button
          data-testid="profile-back-btn"
          onClick={() => navigate('/')}
          className="mb-4 h-9 px-3 rounded-xl border-2 border-[#3a1064] bg-white text-[#3a1064] font-black text-sm shadow-[0_3px_0_#3a1064] flex items-center gap-1.5 hover:bg-yellow-50"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={3} /> Back to tracker
        </button>

        {/* Profile card */}
        <div className="rounded-2xl bg-white border-[3px] border-[#3a1064] shadow-[0_8px_0_#3a1064] overflow-hidden">
          <div
            className="p-4 sm:p-6 flex items-center gap-4 flex-wrap"
            style={{
              background:
                'linear-gradient(135deg,#ffd900,#ff2bd6 50%,#9d3dff)',
            }}
          >
            <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl bg-white border-[3px] border-[#3a1064] shadow-[0_4px_0_#3a1064] overflow-hidden flex items-center justify-center text-3xl sm:text-4xl font-black text-[#ff2bd6]">
              {user?.picture ? (
                <img src={user.picture} alt="Your profile" className="h-full w-full object-cover" />
              ) : (
                initial
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-white font-black text-xl sm:text-2xl drop-shadow-[0_2px_0_#3a1064] truncate">
                {user?.name || user?.username || user?.email}
              </div>
              <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                {user?.username && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-[#3a1064] text-yellow-300 border-2 border-yellow-300">
                    <AtSign className="h-3 w-3" /> {user.username}
                  </span>
                )}
                {isAdmin && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-yellow-300 text-[#3a1064] border-2 border-[#3a1064]">
                    <Shield className="h-3 w-3" /> Admin
                  </span>
                )}
              </div>
            </div>
          </div>
          {user?.email && (
            <div className="p-3 sm:p-4 bg-yellow-50 border-t-2 border-[#3a1064]/15 flex items-center gap-2 text-[#3a1064] font-bold text-sm">
              <Mail className="h-4 w-4 shrink-0" />
              <span className="truncate">{user.email}</span>
            </div>
          )}

          {/* Share-my-profile row */}
          {profileUrl && (
            <div className="p-3 sm:p-4 bg-white border-t-2 border-[#3a1064]/15">
              <div className="text-[10px] font-black uppercase tracking-[2px] text-[#3a1064]/60 mb-1.5">
                Your public trade link
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div
                  data-testid="profile-share-url"
                  className="flex-1 min-w-0 truncate font-black text-sm text-[#3a1064] bg-yellow-50 border-2 border-[#3a1064]/25 rounded-lg px-2.5 py-2"
                >
                  {profileUrl}
                </div>
                <button
                  data-testid="copy-profile-link-btn"
                  onClick={copyProfileLink}
                  className={`h-10 px-3 rounded-lg border-2 border-[#3a1064] font-black text-xs shadow-[0_3px_0_#3a1064] flex items-center gap-1.5 shrink-0 ${
                    copied ? 'bg-emerald-500 text-white' : 'bg-white hover:bg-yellow-50 text-[#3a1064]'
                  }`}
                >
                  {copied
                    ? <><Check className="h-4 w-4" strokeWidth={3} /> Copied</>
                    : <><Copy className="h-4 w-4" strokeWidth={3} /> Copy</>}
                </button>
                <button
                  data-testid="share-profile-btn"
                  onClick={shareProfile}
                  className="h-10 px-3 rounded-lg border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black text-xs shadow-[0_3px_0_#3a1064] flex items-center gap-1.5 shrink-0"
                >
                  <Share2 className="h-4 w-4" strokeWidth={3} /> Share
                </button>
              </div>
              <p className="text-[11px] font-bold text-[#3a1064]/70 mt-2 leading-tight">
                Anyone opening this link lands directly on your trade page with a one-tap <span className="text-[#ff2bd6]">Trade with @{user.username}</span> button.
              </p>
            </div>
          )}
          {!user?.username && (
            <div className="p-3 sm:p-4 bg-yellow-50 border-t-2 border-[#3a1064]/15 text-[11px] font-bold text-[#3a1064]/70">
              Set a Sprite Vault username in the sign-up flow to unlock your shareable trade link.
            </div>
          )}
        </div>

        {/* Account settings — edit name/username + login info */}
        <AccountSettingsCards user={user} onSaved={refresh} />

        {/* Creators section — the incentive lives here on the profile */}
        <div className="mt-4 -mx-3 sm:-mx-6">
          <CreatorsSection />
        </div>
      </div>
    </div>
  );
}


// ─── Account Settings cards ──────────────────────────────────────────────────
function AccountSettingsCards({ user, onSaved }) {
  const isGoogle = user?.provider === 'google';
  return (
    <div className="mt-4 space-y-3">
      <ProfilePictureCard user={user} onSaved={onSaved} />
      <EditProfileCard user={user} onSaved={onSaved} />
      {!isGoogle && <ChangeEmailCard user={user} onSaved={onSaved} />}
      {!isGoogle && <ChangePasswordCard onSaved={onSaved} />}
      {isGoogle && (
        <div className="rounded-2xl bg-white border-[3px] border-[#3a1064] shadow-[0_5px_0_#3a1064] p-4 flex items-start gap-3">
          <Lock className="h-5 w-5 text-[#00b8ff] shrink-0 mt-0.5" strokeWidth={3} />
          <div className="text-xs font-bold text-[#3a1064]/80 leading-snug">
            Your account is signed in with <span className="font-black">Google</span>. Manage your email
            and password from your Google account — Sprite Vault syncs automatically.
          </div>
        </div>
      )}
    </div>
  );
}

function SectionCard({ title, icon, testId, children }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl bg-white border-[3px] border-[#3a1064] shadow-[0_5px_0_#3a1064] overflow-hidden"
    >
      <div className="p-3 sm:p-4 bg-[#ffd900] border-b-2 border-[#3a1064]/15 flex items-center gap-2">
        {icon}
        <div className="font-black text-[#3a1064] text-sm">{title}</div>
      </div>
      <div className="p-3 sm:p-4 space-y-2.5">{children}</div>
    </div>
  );
}

function EditProfileCard({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [username, setUsername] = useState(user?.username || '');
  const [busy, setBusy] = useState(false);

  const cancel = () => {
    setName(user?.name || '');
    setUsername(user?.username || '');
    setOpen(false);
  };

  const save = async () => {
    const body = {};
    if ((name || '').trim() !== (user?.name || '')) body.name = name.trim();
    if ((username || '').trim() !== (user?.username || '')) body.username = username.trim();
    if (Object.keys(body).length === 0) { setOpen(false); return; }
    setBusy(true);
    try {
      await api.updateProfile(body);
      toast({ title: 'Profile updated', description: 'Your changes are saved.' });
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast({ title: 'Update failed', description: e.message });
    } finally { setBusy(false); }
  };

  return (
    <SectionCard title="Display name & username" icon={<Edit3 className="h-4 w-4 text-[#3a1064]" />} testId="profile-edit-card">
      {!open ? (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="min-w-0">
            <div className="font-black text-[#3a1064] truncate">{user?.name || 'Add a display name'}</div>
            <div className="text-xs font-bold text-[#3a1064]/70 truncate">@{user?.username || '—'}</div>
          </div>
          <button
            data-testid="edit-profile-btn"
            onClick={() => setOpen(true)}
            className="h-9 px-3 rounded-lg border-2 border-[#3a1064] bg-white hover:bg-yellow-50 text-[#3a1064] font-black text-xs shadow-[0_3px_0_#3a1064] flex items-center gap-1.5"
          >
            <Edit3 className="h-3.5 w-3.5" strokeWidth={3} /> Edit
          </button>
        </div>
      ) : (
        <div className="space-y-2.5">
          <LabeledInput
            label="Display name"
            value={name}
            onChange={setName}
            placeholder="Your name"
            testId="profile-name-input"
            maxLength={60}
          />
          <LabeledInput
            label="Username (a–z, 0–9, _)"
            value={username}
            onChange={(v) => setUsername(v.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
            placeholder="e.g. sprite_master"
            testId="profile-username-input"
            maxLength={24}
            prefix="@"
          />
          <div className="text-[10px] font-bold text-[#3a1064]/60">
            Your public trade link is <span className="font-black">/u/{username || user?.username || '—'}</span>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              data-testid="profile-save-btn"
              onClick={save}
              disabled={busy}
              className="h-10 px-4 rounded-lg bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black text-sm border-2 border-[#3a1064] shadow-[0_3px_0_#3a1064] flex items-center gap-1.5 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" strokeWidth={3} />}
              Save
            </button>
            <button
              onClick={cancel}
              className="h-10 px-4 rounded-lg bg-white hover:bg-yellow-50 text-[#3a1064] font-black text-sm border-2 border-[#3a1064] flex items-center gap-1.5"
            >
              <X className="h-4 w-4" strokeWidth={3} /> Cancel
            </button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function ChangeEmailCard({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const cancel = () => { setNewEmail(''); setPassword(''); setOpen(false); };

  const save = async () => {
    if (!newEmail.trim() || !password) {
      toast({ title: 'Fill both fields' }); return;
    }
    setBusy(true);
    try {
      await api.changeEmail({ new_email: newEmail.trim(), current_password: password });
      toast({ title: 'Email updated', description: `You'll sign in with ${newEmail.trim()} from now on.` });
      cancel();
      onSaved?.();
    } catch (e) {
      toast({ title: 'Failed to change email', description: e.message });
    } finally { setBusy(false); }
  };

  return (
    <SectionCard title="Login email" icon={<Mail className="h-4 w-4 text-[#3a1064]" />} testId="change-email-card">
      {!open ? (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="min-w-0 flex items-center gap-2 text-[#3a1064] font-bold text-sm">
            <Mail className="h-4 w-4 shrink-0" />
            <span className="truncate">{user?.email}</span>
          </div>
          <button
            data-testid="change-email-btn"
            onClick={() => setOpen(true)}
            className="h-9 px-3 rounded-lg border-2 border-[#3a1064] bg-white hover:bg-yellow-50 text-[#3a1064] font-black text-xs shadow-[0_3px_0_#3a1064]"
          >
            Change email
          </button>
        </div>
      ) : (
        <div className="space-y-2.5">
          <LabeledInput
            label="New email"
            value={newEmail}
            onChange={setNewEmail}
            placeholder="you@example.com"
            testId="new-email-input"
            type="email"
          />
          <LabeledInput
            label="Current password (for security)"
            value={password}
            onChange={setPassword}
            placeholder="••••••••"
            testId="email-current-password-input"
            type={showPw ? 'text' : 'password'}
            trailingButton={
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="h-6 px-1 text-[#3a1064]/60 hover:text-[#3a1064]"
                aria-label={showPw ? 'Hide password' : 'Show password'}
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            }
          />
          <div className="flex gap-2 pt-1">
            <button
              data-testid="save-email-btn"
              onClick={save}
              disabled={busy}
              className="h-10 px-4 rounded-lg bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black text-sm border-2 border-[#3a1064] shadow-[0_3px_0_#3a1064] flex items-center gap-1.5 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" strokeWidth={3} />}
              Update email
            </button>
            <button
              onClick={cancel}
              className="h-10 px-4 rounded-lg bg-white hover:bg-yellow-50 text-[#3a1064] font-black text-sm border-2 border-[#3a1064]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function ChangePasswordCard({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const cancel = () => { setCurrent(''); setNext(''); setConfirm(''); setOpen(false); };

  const save = async () => {
    if (!current || !next || !confirm) { toast({ title: 'Fill every field' }); return; }
    if (next.length < 6) { toast({ title: 'New password must be at least 6 characters' }); return; }
    if (next !== confirm) { toast({ title: 'Passwords do not match' }); return; }
    setBusy(true);
    try {
      await api.changePassword({ current_password: current, new_password: next });
      toast({ title: 'Password updated', description: 'Use your new password next time you sign in.' });
      cancel();
      onSaved?.();
    } catch (e) {
      toast({ title: 'Failed to change password', description: e.message });
    } finally { setBusy(false); }
  };

  return (
    <SectionCard title="Password" icon={<Lock className="h-4 w-4 text-[#3a1064]" />} testId="change-password-card">
      {!open ? (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[#3a1064] font-bold text-sm flex items-center gap-2">
            <Lock className="h-4 w-4" /> ••••••••
          </div>
          <button
            data-testid="change-password-btn"
            onClick={() => setOpen(true)}
            className="h-9 px-3 rounded-lg border-2 border-[#3a1064] bg-white hover:bg-yellow-50 text-[#3a1064] font-black text-xs shadow-[0_3px_0_#3a1064]"
          >
            Change password
          </button>
        </div>
      ) : (
        <div className="space-y-2.5">
          <LabeledInput
            label="Current password"
            value={current}
            onChange={setCurrent}
            placeholder="••••••••"
            testId="current-password-input"
            type={showPw ? 'text' : 'password'}
            trailingButton={
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="h-6 px-1 text-[#3a1064]/60 hover:text-[#3a1064]"
                aria-label={showPw ? 'Hide passwords' : 'Show passwords'}
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            }
          />
          <LabeledInput
            label="New password (min 6 chars)"
            value={next}
            onChange={setNext}
            placeholder="••••••••"
            testId="new-password-input"
            type={showPw ? 'text' : 'password'}
          />
          <LabeledInput
            label="Confirm new password"
            value={confirm}
            onChange={setConfirm}
            placeholder="••••••••"
            testId="confirm-password-input"
            type={showPw ? 'text' : 'password'}
          />
          <div className="flex gap-2 pt-1">
            <button
              data-testid="save-password-btn"
              onClick={save}
              disabled={busy}
              className="h-10 px-4 rounded-lg bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black text-sm border-2 border-[#3a1064] shadow-[0_3px_0_#3a1064] flex items-center gap-1.5 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" strokeWidth={3} />}
              Update password
            </button>
            <button
              onClick={cancel}
              className="h-10 px-4 rounded-lg bg-white hover:bg-yellow-50 text-[#3a1064] font-black text-sm border-2 border-[#3a1064]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function LabeledInput({ label, value, onChange, placeholder, testId, type = 'text', maxLength, prefix, trailingButton }) {
  return (
    <label className="block">
      <div className="text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70 mb-1">{label}</div>
      <div className="flex items-center gap-1 h-10 px-2.5 rounded-lg border-2 border-[#3a1064] bg-white focus-within:ring-2 focus-within:ring-[#ff2bd6]">
        {prefix && <span className="font-black text-[#3a1064]/70">{prefix}</span>}
        <input
          data-testid={testId}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          className="flex-1 min-w-0 bg-transparent font-bold text-[#3a1064] outline-none placeholder:text-[#3a1064]/30"
        />
        {trailingButton}
      </div>
    </label>
  );
}
